import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ArrowRight, Mic2, Clock, Users, Sliders, ChevronLeft, ChevronRight, X } from "lucide-react";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.1 } }),
};

const brands = ["FL STUDIO", "RODE", "SE ELECTRONICS", "PRESONUS", "KALI AUDIO", "BEYERDYNAMIC", "AUDIENT", "BEHRINGER", "AKG", "NOVATION", "SHURE", "CLOUDIFIER", "SSL", "LOGIC PRO", "ARTURIA", "YAMAHA", "NEUMANN"];

const STUDIO_GALLERY = [
  { url: "https://images.pexels.com/photos/36936571/pexels-photo-36936571.jpeg", caption: "The main control desk" },
  { url: "https://images.unsplash.com/photo-1511379938547-c1f69419868d", caption: "Instruments at the ready" },
  { url: "https://images.pexels.com/photos/8198135/pexels-photo-8198135.jpeg", caption: "Cozy monitoring corner" },
  { url: "https://images.unsplash.com/photo-1763771757355-d2a395b5f8ea", caption: "On air" },
  { url: "https://images.unsplash.com/photo-1764922281042-d1148a7e1983", caption: "Warm, vibey lounge" },
  { url: "https://images.pexels.com/photos/8197364/pexels-photo-8197364.jpeg", caption: "Analog tape character" },
  { url: "https://images.unsplash.com/photo-1627407660893-fe01f60d44c4", caption: "The live space" },
];

export default function Landing() {
  const [rooms, setRooms] = useState([]);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);

  useEffect(() => {
    api.get("/rooms").then((r) => setRooms(r.data)).catch(() => {});
  }, []);

  const openGallery = () => { setGalleryIndex(0); setGalleryOpen(true); };
  const nextImg = () => setGalleryIndex((i) => (i + 1) % STUDIO_GALLERY.length);
  const prevImg = () => setGalleryIndex((i) => (i - 1 + STUDIO_GALLERY.length) % STUDIO_GALLERY.length);

  useEffect(() => {
    if (!galleryOpen) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") nextImg();
      if (e.key === "ArrowLeft") prevImg();
      if (e.key === "Escape") setGalleryOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [galleryOpen]);

  return (
    <div className="grain min-h-screen relative">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[88vh] min-h-[600px] flex items-end overflow-hidden">
        <img src="https://images.pexels.com/photos/6707302/pexels-photo-6707302.jpeg"
          alt="Studio mixer" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-black/60 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-transparent" />
        <div className="relative px-6 md:px-12 lg:px-20 pb-20 max-w-4xl">
          <motion.p variants={fade} initial="hidden" animate="show"
            className="text-xs font-semibold uppercase tracking-[0.3em] text-[#D90429] mb-5">
            Vibey Recording Facility
          </motion.p>
          <motion.h1 variants={fade} initial="hidden" animate="show" custom={1}
            className="font-display text-5xl md:text-7xl font-extrabold tracking-tighter leading-[0.95]">
            Where sound<br />becomes <span className="text-[#D90429] text-glow">legacy.</span>
          </motion.h1>
          <motion.p variants={fade} initial="hidden" animate="show" custom={2}
            className="mt-6 text-base md:text-lg text-[#A0A0A0] max-w-xl leading-relaxed">
            Book a cozy studio by the hour. Analog warmth, pristine acoustics and
            engineers who know how to make records. Reserve online — pay at the studio.
          </motion.p>
          <motion.div variants={fade} initial="hidden" animate="show" custom={3} className="mt-10 flex flex-wrap gap-4">
            <Link to="/book" data-testid="hero-book-button"
              className="bg-[#D90429] hover:bg-[#ED2939] text-white px-8 py-4 font-bold uppercase tracking-wide transition-all hover:-translate-y-1 inline-flex items-center gap-2">
              Book a Session <ArrowRight className="h-4 w-4" />
            </Link>
            <button onClick={openGallery} data-testid="hero-explore-button"
              className="border border-white/20 hover:border-white text-white px-8 py-4 font-bold uppercase tracking-wide transition-all">
              Explore the Studio
            </button>
          </motion.div>
        </div>
      </section>

      {/* Marquee */}
      <div className="border-y border-white/10 py-5 overflow-hidden bg-black relative z-10">
        <div className="flex w-max animate-marquee">
          {[...brands, ...brands].map((b, i) => (
            <span key={`${b}-${i}`} className="mx-10 text-sm font-semibold uppercase tracking-[0.3em] text-[#A0A0A0] whitespace-nowrap">{b}</span>
          ))}
        </div>
      </div>

      {/* Rooms showcase */}
      <section className="px-6 md:px-12 lg:px-20 py-24 relative z-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#D90429] mb-3">The Studio</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight">One room. One obsession.</h2>
          </div>
          <Link to="/studios" className="text-sm uppercase tracking-[0.2em] text-[#A0A0A0] hover:text-white transition-colors inline-flex items-center gap-2">
            View all <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="flex flex-wrap justify-center gap-6">
          {rooms.map((room, i) => (
            <motion.div key={room.room_id} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} className="w-full sm:w-80">
              <Link to="/book" data-testid={`landing-room-${room.room_id}`}
                className="group block relative overflow-hidden border border-white/5 hover:border-[#D90429]/50 transition-all">
                <div className="aspect-[4/5] overflow-hidden">
                  <img src={room.image} alt={room.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                <div className="absolute bottom-0 left-0 p-7">
                  <p className="text-xs uppercase tracking-[0.2em] text-[#D90429] mb-2">{room.tagline}</p>
                  <h3 className="font-display text-2xl font-bold">{room.name}</h3>
                  <p className="text-sm text-[#A0A0A0] mt-1">RON {room.price_per_hour}/hr · up to {room.capacity}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="px-6 md:px-12 lg:px-20 py-20 border-t border-white/10 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          {[
            { icon: Sliders, t: "Analog Signal Chain", d: "Vintage consoles and outboard gear for that record-store warmth." },
            { icon: Mic2, t: "Engineers Available", d: "Recording, mixing and mastering pros at your service." },
            { icon: Clock, t: "Flexible Packages", d: "Hourly, half-day and full-day rates with real discounts." },
            { icon: Users, t: "Pay at Studio", d: "Reserve your slot online, settle up when you arrive. No cards needed." },
          ].map((f, i) => (
            <motion.div key={f.t} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}>
              <f.icon className="h-8 w-8 text-[#D90429] mb-4" strokeWidth={1.5} />
              <h3 className="font-display text-lg font-bold mb-2">{f.t}</h3>
              <p className="text-sm text-[#A0A0A0] leading-relaxed">{f.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* About Us */}
      <section id="about" className="px-6 md:px-12 lg:px-20 py-24 border-t border-white/10 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }}>
            <div className="aspect-[4/3] overflow-hidden border border-white/10">
              <img src="https://images.unsplash.com/photo-1627407660893-fe01f60d44c4" alt="Inside the studio"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
            </div>
          </motion.div>
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1}>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#D90429] mb-4">About Us</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight mb-6">We love to create.</h2>
            <p className="text-[#A0A0A0] leading-relaxed">
              With a career spanning over 15 years, Ceinaru has solidified his position as one of the industry's most sought-after beatmakers and sonic architects. His music spans across genres like electronic, dark pop, and hip-hop. Co-founder and ex-member of Beat Reapers Entertainment, Ceinaru is a part time producer, mixing engineer, master engineer and MC. Ceinaru's production credits include collaborations with renowned artists like Guess Who, Macanache, Alan &amp; Kepa, Skizzo Skills, aero, Reckaze, SickSound Crew and many other Romanian artists, delivering chart-topping hits and timeless anthems that have garnered millions of streams worldwide and contributed to the overall sound and success of the songs performed by the artists. With a catalog of hidden gems waiting to be discovered, Ceinaru invites you to explore their world and experience the unparalleled artistry that makes him an under-appreciated force in hip-hop.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-12 lg:px-20 py-24 relative z-10">
        <div className="border border-white/10 bg-gradient-to-br from-[#1A0306] to-[#0A0A0A] p-12 md:p-20 text-center">
          <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight">Ready to hit record?</h2>
          <p className="mt-4 text-[#A0A0A0] max-w-lg mx-auto">Lock in your session in under a minute. Our team confirms every booking personally.</p>
          <Link to="/book" data-testid="cta-book-button"
            className="mt-8 inline-flex items-center gap-2 bg-[#D90429] hover:bg-[#ED2939] text-white px-10 py-4 font-bold uppercase tracking-wide transition-all hover:-translate-y-1">
            Book Now <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <AnimatePresence>
        {galleryOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex flex-col"
            data-testid="studio-gallery-modal" onClick={() => setGalleryOpen(false)}>
            <div className="flex items-center justify-between px-6 md:px-12 py-6">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#D90429]">BNB Studio · {galleryIndex + 1} / {STUDIO_GALLERY.length}</p>
              <button onClick={() => setGalleryOpen(false)} data-testid="gallery-close"
                className="h-10 w-10 flex items-center justify-center border border-white/20 hover:border-white text-white transition-all">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 flex items-center justify-center px-4 md:px-16 pb-6 relative" onClick={(e) => e.stopPropagation()}>
              <button onClick={prevImg} data-testid="gallery-prev"
                className="absolute left-3 md:left-8 z-10 h-12 w-12 flex items-center justify-center bg-black/60 border border-white/20 hover:border-[#D90429] hover:text-[#D90429] text-white transition-all">
                <ChevronLeft className="h-6 w-6" />
              </button>

              <AnimatePresence mode="wait">
                <motion.figure key={galleryIndex}
                  initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.3 }} className="max-w-5xl w-full">
                  <img src={STUDIO_GALLERY[galleryIndex].url} alt={STUDIO_GALLERY[galleryIndex].caption}
                    className="w-full max-h-[70vh] object-contain border border-white/10" />
                  <figcaption className="mt-4 text-center text-sm text-[#A0A0A0] uppercase tracking-[0.2em]">{STUDIO_GALLERY[galleryIndex].caption}</figcaption>
                </motion.figure>
              </AnimatePresence>

              <button onClick={nextImg} data-testid="gallery-next"
                className="absolute right-3 md:right-8 z-10 h-12 w-12 flex items-center justify-center bg-black/60 border border-white/20 hover:border-[#D90429] hover:text-[#D90429] text-white transition-all">
                <ChevronRight className="h-6 w-6" />
              </button>
            </div>

            <div className="flex items-center justify-center gap-2 px-6 pb-8 overflow-x-auto" onClick={(e) => e.stopPropagation()}>
              {STUDIO_GALLERY.map((img, i) => (
                <button key={img.url} onClick={() => setGalleryIndex(i)} data-testid={`gallery-thumb-${i}`}
                  className={`h-14 w-20 flex-shrink-0 overflow-hidden border transition-all ${i === galleryIndex ? "border-[#D90429]" : "border-white/10 opacity-50 hover:opacity-100"}`}>
                  <img src={img.url} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
}
